import React, { forwardRef, useEffect, useState } from "react";
import { Row, Col } from "antd";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "react-query";
import moment from "moment";

import EditableTable from "../systemTable/EditableTable";
import {
  createTableItem,
  updateTableItem,
  deleteTableItem,
} from "../../features/settings/settingsSlice";
import { setActiveSource } from "../../features/sources/sourcesSlice";
import { listSystemTable } from "../../api/server";

const SystemTable = forwardRef((props, ref) => {
  const {
    source,
    nonEditable,
    fields,
    built_in_non_editable,
    initialSorter,
    showInput,
    sub_table_props,
    subTable,
    identifier,
    subTableIdentifier,
  } = props;
  const dispatch = useDispatch();

  // Adjust source based on whether it's a sub Table or not
  const adjustedSource = !subTable
    ? source
    : source.split("/").slice(-1).join("/");

  // State
  const [changedProps, setChangedProps] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
    order: "descend",
    field: initialSorter,
  });
  const [freeText, setFreeText] = useState("");

  // Selectors
  const activeSource = useSelector((state) => state.sources[source]);
  const { name, updateTime } = activeSource || {};
  const createLoading = useSelector(
    (state) => state.pendingActions["systemTable/create_item"]
  );
  const updateLoading = useSelector(
    (state) => state.pendingActions["systemTable/update_item"]
  );
  const deleteLoading = useSelector(
    (state) => state.pendingActions["systemTable/delete_item"]
  );

  // Effects
  useEffect(() => {
    dispatch(
      setActiveSource({
        name: source,
      })
    );
  }, [dispatch, source]);

  // Query
  const { current, pageSize, order, field, total } = changedProps;
  const { isLoading, data } = useQuery(
    [{ name, updateTime, current, pageSize, order, field, freeText }],
    async () => {
      const page = {
        current,
        pageSize,
        filter: {
          freeText,
        },
        order: {
          field,
          order,
        },
        source: name,
      };
      const result = await listSystemTable(page);
      setChangedProps({ ...changedProps, total: result.data.total });
      return result.data.items;
    },
    {
      keepPreviousData: true,
      staleTime: 0, //no cached time
      enabled: name !== null && name !== undefined,
    }
  );

  // Handlers
  const onSave = async (record) => {
    const params = {
      source: adjustedSource,
      record: { ...record, [identifier]: subTableIdentifier },
    };
    await dispatch(
      record.id !== undefined
        ? updateTableItem(params)
        : createTableItem(params)
    );
    dispatch(
      setActiveSource({
        name: source,
        updateTime: moment().format(),
      })
    );
  };

  const onCancel = () => {
    dispatch(
      setActiveSource({
        name: source,
        updateTime: moment().format(),
      })
    );
  };

  const onDelete = async (record) => {
    const params = {
      source: adjustedSource,
      record,
    };
    await dispatch(deleteTableItem(params));
    dispatch(
      setActiveSource({
        name: source,
        updateTime: moment().format(),
      })
    );
  };

  const onTableChange = (tableChangedProps) => {
    const current =
      tableChangedProps?.extra?.action !== "paginate"
        ? 1
        : tableChangedProps.current;
    setChangedProps({ ...tableChangedProps, current });
  };

  const onTextChange = (e) => {
    setFreeText(e.target.value);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }} ref={ref}>
      <Row>
        <Col span={24}>
          <EditableTable
            fields={fields}
            nonEditable={nonEditable}
            built_in_non_editable={built_in_non_editable}
            initialSorter={initialSorter}
            data={data}
            saveRecord={onSave}
            onCancel={onCancel}
            deleteRecord={onDelete}
            loading={
              isLoading || createLoading || updateLoading || deleteLoading
            }
            title={
              subTable ? null : (
                <FormattedMessage id={`app.settings.system_tables.${source}`} />
              )
            }
            onTableChange={onTableChange}
            current={current}
            pageSize={pageSize}
            total={total}
            order={order}
            field={field}
            freeText={freeText}
            onTextChange={onTextChange}
            showInput={showInput}
            sub_table_props={sub_table_props}
            source={source}
          />
        </Col>
      </Row>
    </div>
  );
});

export default SystemTable;
